import { Link } from 'react-router-dom';
import { LeaveRequestUserInfoIncluded } from '../../../commonTypes/acs';
import cc from 'classcat';
import { convertIsoToMonthDay, convertIsoToMonthDayInterval, convertIsoToDiffDaysWithoutWeekends } from '../../../util/date/dateHelper';
import { UserWithPicture } from '../UserWithPicture';
import { Accordion, Button, AccordionItem, StatusColor } from '../../Common';
import { LeaveRequestStatus, LeaveRequestType } from '../../../commonTypes/leaveRequest.types';
import { useTranslation } from '../../../hooks/useTranslation';

export type AccordionVariation = 'standard' | 'forOwner';

interface IRequestAccordion {
    request: LeaveRequestUserInfoIncluded;
    openDeleteModal?: (id: string, requestType?: LeaveRequestType) => void;
    openEditModal?: () => void;
    rejectRequest?: (id: string) => void;
    approveRequest?: (id: string) => void;
    variant?: AccordionVariation;
}

interface IRequestAccordionHeader {
    request: LeaveRequestUserInfoIncluded;
    variant?: AccordionVariation;
}

export const RequestAccordion = ({
    request,
    openDeleteModal,
    rejectRequest,
    approveRequest,
    variant = 'standard',
    openEditModal,
}: IRequestAccordion) => {
    const { t } = useTranslation();

    const renderLastItem = () => {
        if (variant === 'standard' && openDeleteModal && openEditModal)
            return (
                <AccordionItem
                    title={'Options'}
                    value={
                        <div className="request-accordion__option-buttons">
                            <button onClick={() => openEditModal()}>
                                <img src="/svg/pen-grey.svg" alt="Pen" />
                            </button>
                            <button onClick={() => openDeleteModal(request.id, request.policyType as LeaveRequestType)}>
                                <img src="/svg/close-square.svg" alt="Remove" />
                            </button>
                        </div>
                    }
                />
            );
        else if (request.status === LeaveRequestStatus.PENDING && approveRequest && rejectRequest)
            return (
                <div className="accordion__buttons">
                    <Button text={t('leaveRequest.reject')} onClick={() => rejectRequest(request.id)} variant="text" color="error" />
                    <Button text={t('leaveRequest.approve')} onClick={() => approveRequest(request.id)} variant="contained" color="success" />
                </div>
            );
    };

    return (
        <Accordion headerElement={<RequestAccordionHeader request={request} variant={variant} />}>
            <AccordionItem title={t('leaveRequest.requestDate')} value={convertIsoToMonthDay(request.createdAt)} />
            <AccordionItem title={t('leaveRequest.policy')} value={request.policyType} blueText />
            <AccordionItem title={t('leaveRequest.period')} value={convertIsoToMonthDayInterval(request.fromDate, request.toDate)} />
            <AccordionItem
                title={t('leaveRequest.capitalize_days')}
                value={`${convertIsoToDiffDaysWithoutWeekends(request.fromDate, request.toDate)} ${t('leaveRequest.dayUnit')}`}
            />
            <AccordionItem title={t('common.description')} value={request.description ?? t('leaveRequest.noDescription')} />
            <AccordionItem title={t('leaveRequest.status')} value={request.status} statusColor={request.status.toLocaleLowerCase() as StatusColor} />
            {request.rejectReason && <AccordionItem title={t('leaveRequest.rejectionReason')} value={request.rejectReason} vertical />}
            {renderLastItem()}
        </Accordion>
    );
};

export const RequestAccordionHeader = ({ request, variant }: IRequestAccordionHeader) => {
    return (
        <>
            {variant === 'standard' ? (
                <p className={cc(['paragraph-3', 'accordion__title', `accordion__title--${request.status.toLocaleLowerCase()}`])}>
                    {convertIsoToMonthDayInterval(request.fromDate, request.toDate)}
                </p>
            ) : (
                <div className={cc(['accordion__user', `accordion__user--${request.status.toLocaleLowerCase()}`])}>
                    <Link to={`/profile/${request.userSub}`}>
                        <UserWithPicture
                            email={request.email}
                            firstName={request.givenName}
                            lastName={request.familyName}
                            imgUrl={request.imageUrl}
                        />
                    </Link>
                </div>
            )}
        </>
    );
};
